import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useKidsStore from '../store/kidsStore';
import { mediaSrc } from '../api/media';
import ProgressRing from '../components/ProgressRing';
import SubjectsRing, { SubjectRingItem } from '../components/SubjectsRing';
import ActivityFeed from '../components/ActivityFeed';
import InfoBanner from '../components/InfoBanner';

type HomeTab = 'progress' | 'activities';

interface KidProgress {
  math: number | null;
  reading: number | null;
  homework: number | null;
  subjects: SubjectRingItem[];
}

const avg = (arr: { percentage: number }[]): number | null =>
  arr.length === 0 ? null : arr.reduce((s, r) => s + (r.percentage || 0), 0) / arr.length;

const ParentHome: React.FC = () => {
  const navigate = useNavigate();
  const { kids, fetchKids } = useKidsStore();
  const [tab, setTab] = useState<HomeTab>('progress');
  const [progress, setProgress] = useState<Record<number, KidProgress>>({});
  // 'self' or a kid id for the Activities picker
  const [feed, setFeed] = useState<'self' | number>('self');

  useEffect(() => { fetchKids(); }, [fetchKids]);

  useEffect(() => {
    kids.forEach((k) => {
      api.get(`/api/parent/kids/${k.id}/activity`).then((r) => {
        const d = r.data;
        const hw = d.homework || [];
        setProgress((p) => ({
          ...p,
          [k.id]: {
            math: avg(d.mathResults || []),
            reading: avg(d.readingResults || []),
            homework: hw.length === 0 ? null : (hw.filter((h: any) => h.status === 'DONE').length / hw.length) * 100,
            subjects: (d.subjectAverages || []).map((s: any) => ({
              name: s.subjectName, percent: s.percentage, attempts: s.attempts,
            })),
          },
        }));
      }).catch(() => {});
    });
  }, [kids]);

  return (
    <div className="space-y-5">
      {/* Tab switcher */}
      <div className="flex gap-2 bg-white/70 backdrop-blur rounded-full p-1 shadow-sm">
        <button
          onClick={() => setTab('progress')}
          className={`kt-tab flex-1 ${tab === 'progress' ? 'bg-brand-yellow text-gray-900 shadow' : 'text-gray-500'}`}
        >
          📊 KidsTrack Progress
        </button>
        <button
          onClick={() => setTab('activities')}
          className={`kt-tab flex-1 ${tab === 'activities' ? 'bg-brand-blue text-white shadow' : 'text-gray-500'}`}
        >
          ✨ Activities
        </button>
      </div>

      {tab === 'progress' ? (
        kids.length === 0 ? (
          <InfoBanner
            tone="blue"
            icon="👋"
            title="Add your first child to get started"
            steps={[
              'Go to the KidsTrack tab.',
              'Tap “+ Add Kid” and enter their name, a username, a password and grade.',
              'Share that username & password — your child uses them to log in.',
            ]}
            cta={{ label: 'Go to KidsTrack', onClick: () => navigate('/parent/learn') }}
          />
        ) : (
          <div className="space-y-4">
            {kids.map((k) => {
              const p = progress[k.id] || { math: null, reading: null, homework: null, subjects: [] };
              return (
                <section key={k.id} className="kt-card p-4">
                  <button
                    onClick={() => navigate(`/parent/kids/${k.id}/learn`)}
                    className="flex items-center gap-3 mb-3 w-full text-left"
                  >
                    {k.profilePhotoUrl ? (
                      <img src={mediaSrc(k.profilePhotoUrl)} alt={k.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-brand-purple/30" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-purple-100 flex items-center justify-center text-xl">🧒</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-gray-800 truncate">{k.name}</p>
                      <p className="text-xs text-gray-500">Grade {k.grade}</p>
                    </div>
                    <span className="text-gray-300 text-xl">›</span>
                  </button>
                  <div className="grid grid-cols-2 gap-3 justify-items-center">
                    <ProgressRing percent={p.math} label="Math" icon="🧮" gradient={['#C084FC', '#7C3AED']} size={92} strokeWidth={9} />
                    <ProgressRing percent={p.reading} label="Reading" icon="📖" gradient={['#FDBA74', '#F97316']} size={92} strokeWidth={9} />
                    <ProgressRing percent={p.homework} label="Homework" icon="📚" gradient={['#7DD3FC', '#2563EB']} size={92} strokeWidth={9} />
                    <SubjectsRing subjects={p.subjects} size={92} />
                  </div>
                </section>
              );
            })}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {/* Picker: Me + each kid */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            <PickChip active={feed === 'self'} onClick={() => setFeed('self')} label="Me" emoji="👤" />
            {kids.map((k) => (
              <PickChip key={k.id} active={feed === k.id} onClick={() => setFeed(k.id)} label={k.name} emoji="🧒" />
            ))}
          </div>
          {feed === 'self' ? (
            <ActivityFeed endpoint="/api/parent/activities" />
          ) : (
            <ActivityFeed kidId={feed} />
          )}
        </div>
      )}
    </div>
  );
};

const PickChip: React.FC<{ active: boolean; onClick: () => void; label: string; emoji: string }> = ({ active, onClick, label, emoji }) => (
  <button
    onClick={onClick}
    className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-bold transition ${
      active ? 'bg-brand-blue text-white shadow' : 'bg-white text-gray-600 shadow-sm'
    }`}
  >
    {emoji} {label}
  </button>
);

export default ParentHome;
